/**
 * External Communication Service using Valtio
 * Replaces RxJS Subjects with modern reactive state management
 */

import { proxy } from 'valtio';

import { trackerManager } from '../managers/TrackerManager';
import type { IExternalChannel } from 'types/external/IExternalChannel';
import type { IStartGameParams } from 'types/store/IStartGameStore';

interface ExternalState {
  channel: IExternalChannel | null;
  isVisible: boolean;
  soundMuted: boolean;
  bgMusicMuted: boolean;
  defenseCode: string | null;
  lastMessage: IExternalChannel | null;
  lastPlayedGames: string[];
}

type MessageHandler<TValue = unknown> = (value: TValue) => void;
type AllMessagesHandler = (event: string, value: unknown) => void;

export const externalState = proxy<ExternalState>({
  channel: null,
  isVisible: false,
  soundMuted: false,
  bgMusicMuted: false,
  defenseCode: null,
  lastMessage: null,
  lastPlayedGames: [],
});

class ExternalCommunicationService {
  private messageHandlers = new Map<string, MessageHandler>();
  private allMessageHandlers: AllMessagesHandler[] = [];

  // Events that need legacy command format for Game Client compatibility
  private legacyCommandMap: Record<string, string> = {
    exit: 'exit',
    openLobby: 'openLobby',
  };

  constructor() {
    trackerManager.startStep('ExternalCommunicationService initialization');
    this.setupMessageListener();
    trackerManager.completeStep('ExternalCommunicationService initialization');
  }

  private setupMessageListener() {
    window.addEventListener('message', (event) => {
      // Ignore outgoing messages (like old lobby)
      if (event.data?.requestType === 'outgoing') {
        return;
      }

      if (event.data?.event) {
        this.handleExternalMessage(event.data);
      }
    });
  }


  private handleExternalMessage(message: IExternalChannel) {
    externalState.channel = message;
    externalState.lastMessage = message;

    const handler = this.messageHandlers.get(message.event);
    if (handler) {
      handler(message.value);
    }

    // Call all-message handlers
    this.allMessageHandlers.forEach((handler) => {
      handler(message.event, message.value);
    });
  }

  /**
   * Register a message handler for a specific event
   */
  onMessage<TValue>(event: string, handler: MessageHandler<TValue>) {
    this.messageHandlers.set(event, handler as MessageHandler);
  }

  /**
   * Register a handler for all messages (wildcard)
   */
  onAllMessages(handler: AllMessagesHandler) {
    this.allMessageHandlers.push(handler);
  }

  /**
   * Remove a handler from all messages
   */
  offAllMessages(handler: AllMessagesHandler) {
    const index = this.allMessageHandlers.indexOf(handler);
    if (index > -1) {
      this.allMessageHandlers.splice(index, 1);
    }
  }

  /**
   * Send a message to the parent window
   */
  sendMessage(event: string, value: unknown) {
    // Check if event has legacy command format
    trackerManager.startStep(`Sending event: ${event}`);
    const legacyCommand = this.legacyCommandMap[event];
    if (legacyCommand) {
      window.parent.postMessage(
        {
          command: legacyCommand,
          requestType: 'outgoing',
        },
        '*',
      );
      trackerManager.completeStep(`Sending event: ${event}`);
      return;
    }

    // Send standard event format for newer events
    const message = {
      event,
      value,
      requestType: 'outgoing', // Add requestType like old lobby
    };
    window.parent.postMessage(message, '*');
    trackerManager.completeStep(`Sending event: ${event}`);
  }

  /**
   * Send display event
   */
  sendDisplay(visible: boolean) {
    this.sendMessage('display', visible);
  }

  /**
   * Send exit event
   */
  sendExit() {
    this.sendMessage('exit', true);
  }

  /**
   * Send lobby initialized event
   */
  sendLobbyInitialized() {
    this.sendMessage('lobbyInitialized', true);
  }

  /**
   * Notify Game Client when lobby is opened
   */
  sendOpenLobby() {
    this.sendMessage('openLobby', true);
  }

  /**
   * Send error event
   */
  sendError(error: unknown) {
    this.sendMessage('error', {
      type: 'LOBBY_ERROR',
      payload: error,
      code: 'LOBBY_ERROR',
    });
  }

  /**
   * Send language change event
   */
  sendLanguageChange(languageCode: string) {
    this.sendMessage('language', languageCode.toLowerCase());
  }

  /**
   * Send mute event
   */
  sendMute(muted: boolean) {
    this.sendMessage('mute', muted);
  }

  /**
   * Send show deposit modal event
   */
  sendShowDeposit() {
    //todo debounse ???
    this.sendMessage('deposit', true);
  }

  /**
   * Send start lobby game event
   */
  sendStartLobbyGame(params: IStartGameParams) {
    this.sendMessage('startLobbyGame', params);
  }

  /**
   * Notify when a game is added to favorites
   */
  sendGameAddedToFavorites(gameKey: string) {
    this.sendMessage('gameAddedToFavorites', gameKey);
  }

  /**
   * Notify when a game is removed from favorites
   */
  sendGameRemovedFromFavorites(gameKey: string) {
    this.sendMessage('gameRemovedFromFavorites', gameKey);
  }

  /**
   * Request defense code for game
   */
  sendDefenseCodeRequired(gameKey: string) {
    this.sendMessage('defenceCodeRequired', gameKey);
  }

  /**
   * Request last played games from parent
   */
  requestLastPlayedGames() {
    this.sendMessage('getLastPlayed', null);
  }
}

export const externalCommunication = new ExternalCommunicationService();

// React hooks for components
// export const useExternalState = () => externalState;
//
// export const useExternalCommunication = () => ({
//   sendMessage: externalCommunication.sendMessage.bind(externalCommunication),
//   sendDisplay: externalCommunication.sendDisplay.bind(externalCommunication),
//   sendExit: externalCommunication.sendExit.bind(externalCommunication),
//   sendLobbyInitialized: externalCommunication.sendLobbyInitialized.bind(externalCommunication),
//   sendOpenLobby: externalCommunication.sendOpenLobby.bind(externalCommunication),
//   sendError: externalCommunication.sendError.bind(externalCommunication),
//   sendLanguageChange: externalCommunication.sendLanguageChange.bind(externalCommunication),
//   sendMute: externalCommunication.sendMute.bind(externalCommunication),
//   sendDepositEnabled: externalCommunication.sendShowDeposit.bind(externalCommunication),
//   sendStartLobbyGame: externalCommunication.sendStartLobbyGame.bind(externalCommunication),
//   sendDefenseCodeRequired: externalCommunication.sendDefenseCodeRequired.bind(externalCommunication),
//   requestLastPlayedGames: externalCommunication.requestLastPlayedGames.bind(externalCommunication),
// });
